import React, { useCallback, useState } from 'react';
import {
  Modal,
  Button,
  Alert,
  Row,
  Select,
  Input as AntInput,
  Upload,
} from 'antd';
import PropTypes from 'prop-types';
import Input from '../../../../components/elements/input/Input';
import BirthDateInput from '../../../../components/elements/birthDate/BirthDateInput';
import { createUser } from '../../api/usersApi';
import { userPickupMethodOptions } from '../../constants/userConstants';
import {
  buildAddressPayload,
  createEmptyAddressForm,
} from '../../utils/addressForm';
import {
  deleteImage,
  uploadDetachedImage,
} from '../../../../shared/api/imageApi';

const manualFieldLabelStyle = {
  display: 'block',
  marginBottom: 6,
  fontWeight: 600,
};

const requiredFieldLabel = (label) => `${label} *`;

// Isti obrazac kao shared/utils/validatePhoneNumber.
const PHONE_PATTERN = /^(\+381|0)[1-9]\d{6,12}$/;
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const shouldBeContactedOptions = [
  { value: true, label: 'Da' },
  { value: false, label: 'Ne' },
];

const CreateUserModal = ({ isOpen, onClose, onCreate }) => {
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [imageLoading, setImageLoading] = useState(false);
  const [consentImage, setConsentImage] = useState(null);

  const [modalForm, setModalForm] = useState({
    firstName: '',
    lastName: '',
    dateOfBirth: '',
    email: '',
    internalNote: '',
    lbo: '',
    healthBookletNumber: '',
    isHealthBookletVerified: true,
    pickupMethod: 'APO',
    shouldBeContacted: true,
    phone: '',
    password: '',
    address: createEmptyAddressForm(),
    shipmentRecipientFullName: '',
    shipmentRecipientPhone: '',
    shipmentNote: '',
  });

  const resetModalForm = () => {
    setModalForm({
      firstName: '',
      lastName: '',
      dateOfBirth: '',
      email: '',
      internalNote: '',
      lbo: '',
      healthBookletNumber: '',
      isHealthBookletVerified: true,
      pickupMethod: 'APO',
      shouldBeContacted: true,
      phone: '',
      password: '',
      address: createEmptyAddressForm(),
      shipmentRecipientFullName: '',
      shipmentRecipientPhone: '',
      shipmentNote: '',
    });
  };

  const resetModalState = () => {
    resetModalForm();
    setConsentImage(null);
  };

  const handleOnChange = (e) => {
    const { name, value } = e.target;
    const nextValue =
      name === 'lbo' || name === 'healthBookletNumber'
        ? value.replace(/\D/g, '')
        : value;

    setModalForm((prev) => {
      return {
        ...prev,
        [name]: nextValue,
      };
    });
  };

  const handleAddressChange = (field, value) => {
    setModalForm((prev) => ({
      ...prev,
      address: {
        ...prev.address,
        [field]: value,
      },
    }));
  };

  const handleOk = useCallback(async () => {
    setLoading(true);
    setError(null);
    setSuccessMessage('');

    try {
      const values = {
        firstName: modalForm.firstName?.trim(),
        lastName: modalForm.lastName?.trim(),
        dateOfBirth: modalForm.dateOfBirth || undefined,
        email: modalForm.email?.trim() || undefined,
        internalNote: modalForm.internalNote?.trim() || undefined,
        role: 'user',
        lbo: modalForm.lbo?.trim(),
        healthBookletNumber: modalForm.healthBookletNumber?.trim(),
        isHealthBookletVerified: modalForm.isHealthBookletVerified,
        pickupMethod: modalForm.pickupMethod || undefined,
        shouldBeContacted: modalForm.shouldBeContacted,
        phone: modalForm.phone?.trim() || undefined,
        password: modalForm.password,
        consentImages: consentImage?.id ? [consentImage.id] : [],
        address: buildAddressPayload(modalForm.address),
        shipmentRecipientFullName:
          modalForm.shipmentRecipientFullName?.trim() || undefined,
        shipmentRecipientPhone:
          modalForm.shipmentRecipientPhone?.trim() || undefined,
        shipmentNote: modalForm.shipmentNote?.trim() || undefined,
      };

      // Iste provere kao na beku (CreateUserDto), ali sa porukom po polju:
      // bek za nevalidan DTO vraca samo opste "Podaci nisu ispravni".
      const validationError = [
        values.phone &&
          !PHONE_PATTERN.test(values.phone) &&
          'Broj telefona nije ispravan (npr. +3816xxxxxxx ili 06xxxxxxx).',
        !values.lbo && 'Popunite obavezno polje: LBO.',
        !values.healthBookletNumber && 'Popunite obavezno polje: BZK.',
        values.dateOfBirth &&
          values.dateOfBirth > new Date().toISOString().slice(0, 10) &&
          'Datum rodjenja ne moze biti u buducnosti.',
        values.email &&
          !EMAIL_PATTERN.test(values.email) &&
          'Email adresa nije ispravna.',
        values.password &&
          values.password.length < 6 &&
          'Lozinka mora imati najmanje 6 karaktera.',
        values.shipmentRecipientPhone &&
          !PHONE_PATTERN.test(values.shipmentRecipientPhone) &&
          'Telefon primaoca posiljke nije ispravan (npr. +3816xxxxxxx ili 06xxxxxxx).',
      ].find(Boolean);

      if (validationError) {
        setError(validationError);
        return;
      }

      if (!values.password) {
        delete values.password;
      }
      if (!values.address) {
        delete values.address;
      }

      const { ok, message } = await createUser(values);

      if (!ok) {
        setError(message);
        return;
      }

      setSuccessMessage(message || 'Pacijent kreiran.');
      setDone(true);
      onCreate();
      resetModalState();

      setTimeout(() => {
        setDone(false);
        onClose();
      }, 1000);
    } catch (err) {
      // Neocekivana greska (ne odgovor beka) - njen tekst je tehnicki / na engleskom.
      console.error(err);
      setError('Doslo je do greske pri kreiranju pacijenta.');
    } finally {
      setLoading(false);
    }
  }, [consentImage?.id, modalForm, onCreate, onClose]);

  const handleConsentUpload = async (file, onSuccess, onError) => {
    setImageLoading(true);
    setError('');

    try {
      if (consentImage?.id) {
        await deleteImage(consentImage.id);
      }

      const { data, error: uploadError } = await uploadDetachedImage(file);

      if (uploadError) {
        setError(uploadError);
        onError?.(uploadError);
        return;
      }

      const uploadedImage = data?.image || data;
      setConsentImage({
        id: uploadedImage?._id,
        url: uploadedImage?.url,
      });
      onSuccess?.(uploadedImage);
    } finally {
      setImageLoading(false);
    }
  };

  const handleRemoveConsentImage = async () => {
    if (!consentImage?.id) {
      setConsentImage(null);
      return;
    }

    setImageLoading(true);
    const { error: deleteError } = await deleteImage(consentImage.id);
    setImageLoading(false);

    if (deleteError) {
      setError(deleteError);
      return;
    }

    setConsentImage(null);
  };

  const handleCancel = async () => {
    if (consentImage?.id) {
      await deleteImage(consentImage.id);
    }

    resetModalState();
    setError('');
    setSuccessMessage('');
    setDone(false);
    onClose();
  };

  return (
    <Modal
      className={'create_user_modal'}
      styles={{ footer: { flexWrap: 'wrap' } }}
      title="Dodaj pacijenta"
      open={isOpen}
      onOk={handleOk}
      onCancel={handleCancel}
      footer={[
        // Poruke idu u footer, u svoj red iznad dugmeta (flexWrap gore): telo
        // modala skroluje iznutra, pa se na dnu tela greska nije videla.
        successMessage && (
          <Alert
            key="success"
            title={successMessage}
            type="success"
            showIcon
            style={{ width: '100%', textAlign: 'left' }}
          />
        ),
        error && (
          <Alert
            key="error"
            title={error}
            type="error"
            showIcon
            style={{ width: '100%', textAlign: 'left' }}
          />
        ),
        <Button
          key="create"
          type="primary"
          onClick={handleOk}
          disabled={loading || done}
          style={{ width: '150px' }}
        >
          {loading ? (
            <img
              src="/assets/icons/add.svg"
              alt="Dodaj"
              className="animate-spin"
            />
          ) : done ? (
            <img
              src="/assets/icons/check.svg"
              alt="Check Icon"
              className="check-icon"
            />
          ) : (
            'Dodaj pacijenta'
          )}
        </Button>,
      ]}
    >
      <div className={'create_user_modal_form'} autoComplete="off">
        <Row className={'create_user_modal_row'}>
          <div>
            <Input
              label="Ime"
              id="firstName"
              name="firstName"
              placeholder="Ime"
              value={modalForm.firstName}
              onChange={(e) => handleOnChange(e)}
            />
          </div>

          <div>
            <Input
              label="Prezime"
              id="lastName"
              name="lastName"
              placeholder="Prezime"
              value={modalForm.lastName}
              onChange={(e) => handleOnChange(e)}
            />
          </div>
          <div>
            <Input
              label="Broj telefona"
              id="phone"
              name="phone"
              placeholder="Broj telefona"
              value={modalForm.phone}
              onChange={(e) => handleOnChange(e)}
            />
          </div>
          <div>
            <Input
              required
              label={requiredFieldLabel('LBO')}
              id="lbo"
              name="lbo"
              placeholder={requiredFieldLabel('Unesite LBO broj')}
              value={modalForm.lbo}
              inputMode="numeric"
              onChange={(e) => handleOnChange(e)}
            />
          </div>
          <div>
            <Input
              required
              label={requiredFieldLabel('BZK')}
              id="healthBookletNumber"
              name="healthBookletNumber"
              placeholder={requiredFieldLabel(
                'Unesite broj zdravstvene knjizice'
              )}
              value={modalForm.healthBookletNumber}
              inputMode="numeric"
              onChange={(e) => handleOnChange(e)}
            />
          </div>
          <div>
            <label htmlFor="isHealthBookletVerified" style={manualFieldLabelStyle}>
              Overena knjizica
            </label>
            <Select
              id="isHealthBookletVerified"
              value={modalForm.isHealthBookletVerified}
              onChange={(value) =>
                setModalForm((prev) => ({
                  ...prev,
                  isHealthBookletVerified: value,
                }))
              }
              style={{ width: '100%' }}
              options={shouldBeContactedOptions}
            />
          </div>
          <div>
            <label htmlFor="pickupMethod" style={manualFieldLabelStyle}>
              {'Na\u010din preuzimanja'}
            </label>
            <Select
              id="pickupMethod"
              placeholder="Način preuzimanja"
              value={modalForm.pickupMethod || undefined}
              onChange={(value) =>
                setModalForm((prev) => ({ ...prev, pickupMethod: value }))
              }
              style={{ width: '100%' }}
              options={userPickupMethodOptions}
            />
          </div>

          <div>
            <label htmlFor="shouldBeContacted" style={manualFieldLabelStyle}>
              Javljati pacijentu?
            </label>
            <Select
              id="shouldBeContacted"
              value={modalForm.shouldBeContacted}
              onChange={(value) =>
                setModalForm((prev) => ({
                  ...prev,
                  shouldBeContacted: value,
                }))
              }
              style={{ width: '100%' }}
              options={shouldBeContactedOptions}
            />
          </div>

          <div>
            <Input
              type="email"
              autoComplete="off"
              label="Email adresa"
              id="email"
              name="email"
              placeholder="Adresa e-poste"
              value={modalForm.email}
              onChange={(e) => handleOnChange(e)}
            />
          </div>

          <div>
            <Input
              type="password"
              autoComplete="new-password"
              label="Lozinka"
              id="password"
              name="password"
              placeholder="Lozinka"
              value={modalForm.password}
              onChange={(e) => handleOnChange(e)}
            />
          </div>

          <div style={{ gridColumn: '1 / -1' }}>
            <label htmlFor="internalNote" style={manualFieldLabelStyle}>
              Interna napomena
            </label>
            <AntInput.TextArea
              id="internalNote"
              name="internalNote"
              placeholder="Unesite internu napomenu"
              value={modalForm.internalNote}
              onChange={handleOnChange}
              autoSize={{ minRows: 3, maxRows: 6 }}
            />
          </div>

          <div>
            <label htmlFor="dateOfBirth" style={manualFieldLabelStyle}>
              {'Datum ro\u0111enja'}
            </label>
            <BirthDateInput
              id="dateOfBirth"
              value={modalForm.dateOfBirth}
              onChange={(v) =>
                setModalForm((prev) => ({
                  ...prev,
                  dateOfBirth: v,
                }))
              }
            />
          </div>

          <div style={{ gridColumn: '1 / -1' }}>
            <label style={manualFieldLabelStyle}>
              Slika saglasnosti
            </label>
            <div
              style={{
                border: '1px solid #d9d9d9',
                borderRadius: 8,
                padding: 12,
                display: 'flex',
                gap: 16,
                alignItems: 'center',
                flexWrap: 'wrap',
              }}
            >
              <img
                src={consentImage?.url || '/assets/icons/consent-placeholder.svg'}
                alt="Saglasnost"
                style={{
                  width: 112,
                  height: 112,
                  objectFit: 'cover',
                  borderRadius: 8,
                  border: '1px solid #f0f0f0',
                  padding: consentImage?.url ? 0 : 12,
                  background: '#fafafa',
                }}
              />
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <Upload
                  accept="image/*"
                  maxCount={1}
                  name="image"
                  showUploadList={false}
                  customRequest={async ({ file, onSuccess, onError }) =>
                    handleConsentUpload(file, onSuccess, onError)
                  }
                >
                  <Button
                    size="small"
                    loading={imageLoading}
                    disabled={loading}
                  >
                    {consentImage ? 'Zameni saglasnost' : 'Dodaj saglasnost'}
                  </Button>
                </Upload>
                {consentImage && (
                  <Button
                    size="small"
                    danger
                    onClick={handleRemoveConsentImage}
                    disabled={imageLoading || loading}
                  >
                    Obrisi
                  </Button>
                )}
              </div>
            </div>
          </div>

          <div style={{ gridColumn: '1 / -1' }}>
            <label style={{ display: 'block', marginBottom: 6 }}>Adresa</label>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                gap: 16,
              }}
            >
              <div>
                <Input
                  label="Ulica"
                  id="addressStreet"
                  name="addressStreet"
                  placeholder="Ulica i broj"
                  value={modalForm.address.street}
                  onChange={(e) =>
                    handleAddressChange('street', e.target.value)
                  }
                />
              </div>
              <div>
                <Input
                  label="Grad"
                  id="addressCity"
                  name="addressCity"
                  placeholder="Grad"
                  value={modalForm.address.city}
                  onChange={(e) => handleAddressChange('city', e.target.value)}
                />
              </div>
              <div>
                <Input
                  label="Postanski broj"
                  id="addressPostalCode"
                  name="addressPostalCode"
                  placeholder="Postanski broj"
                  value={modalForm.address.postalCode}
                  onChange={(e) =>
                    handleAddressChange('postalCode', e.target.value)
                  }
                />
              </div>
              <div>
                <Input
                  label="Drzava"
                  id="addressCountry"
                  name="addressCountry"
                  placeholder="Drzava"
                  value={modalForm.address.country}
                  onChange={(e) =>
                    handleAddressChange('country', e.target.value)
                  }
                />
              </div>
              <div>
                <Input
                  label="Ime i prezime primaoca posiljke"
                  id="shipmentRecipientFullName"
                  name="shipmentRecipientFullName"
                  placeholder="Ime i prezime primaoca posiljke"
                  value={modalForm.shipmentRecipientFullName}
                  onChange={(e) => handleOnChange(e)}
                />
              </div>
              <div>
                <Input
                  label="Telefon primaoca posiljke"
                  id="shipmentRecipientPhone"
                  name="shipmentRecipientPhone"
                  placeholder="Telefon primaoca posiljke"
                  value={modalForm.shipmentRecipientPhone}
                  onChange={(e) => handleOnChange(e)}
                />
              </div>
            </div>
            <div style={{ marginTop: 16 }}>
              <label htmlFor="shipmentNote" style={manualFieldLabelStyle}>
                Napomena o posiljci
              </label>
              <AntInput.TextArea
                id="shipmentNote"
                name="shipmentNote"
                placeholder="Napomena o posiljci"
                value={modalForm.shipmentNote}
                onChange={(e) => handleOnChange(e)}
                autoSize={{ minRows: 3, maxRows: 6 }}
              />
            </div>
          </div>
        </Row>
      </div>
    </Modal>
  );
};

CreateUserModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onCreate: PropTypes.func.isRequired,
};

export default CreateUserModal;
